/**
 * PR #420 round 8: every OTHER fax of the case (any episode) that reached DCF and waits for a person -- shown with its own
 * acts, so an earlier packet's delivery is never hidden behind the current one. The server checks every act again
 * (who may, the sender bar for a resolution, the case's lock).
 *
 *   Try recording again   when offered (an error, or a missing accuracy confirmation): records it from the fax service's report
 *   Settle                ACKNOWLEDGED, with a reason and the person's attestation: it reached DCF, and is not recorded as a sending
 */
import React, { useCallback, useEffect, useState } from 'react';
import { umedApi } from './umedApi';
import type { FaxTransmission } from './umedApi';

type Waiting = FaxTransmission;

const unknownOrSaid = (r: { status?: number; error?: string; message?: string; code?: string }) => (r.status === undefined || r.status >= 500
  ? 'eMerge could not confirm whether this went through. Reload the case to see what was recorded before trying again.'
  : r.error || r.message || (r.code ? `Refused (${r.code}).` : 'The server refused this.'));

function WaitingFax({ caseId, fax, onDone, earlier = true }: { caseId: string; fax: Waiting; onDone: (msg: string) => void | Promise<void>; earlier?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [settling, setSettling] = useState(false);
  const [reason, setReason] = useState('');
  const [attest, setAttest] = useState(false);
  const options: string[] = fax.resolve_options || [];
  const canRetry = Boolean(fax.can_retry_recording && options.includes('RETRY_RECORDING'));
  const canSettle = Boolean(fax.can_resolve && options.includes('ACKNOWLEDGED'));
  const trimmed = reason.trim();
  const run = async (fn: () => Promise<{ success: boolean; status?: number; error?: string; message?: string; code?: string; data?: { sending?: string | null } }>, done: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await fn();
      if (!r.success) { setErr(unknownOrSaid(r)); return; }
      await onDone(done);
    } finally { setBusy(false); }
  };
  return (
    <div className="um-notice stop" role="status" data-testid="fax-waiting">
      <p>
        <strong>{earlier ? 'An earlier fax reached DCF and waits for a person' : 'A fax of this case reached DCF and waits for a person'}</strong> · revision {fax.packet_version ?? '?'} · {fax.completed_at ? new Date(fax.completed_at).toLocaleDateString('en-US') : ''}
      </p>
      {fax.sending_reason && <p>Not recorded as the case&rsquo;s sending: {fax.sending_reason}.</p>}
      <p>
        {canRetry && <button type="button" className="um-open um-primary" disabled={busy} onClick={() => run(() => umedApi.retryFaxRecording(caseId, fax.id), 'Recorded again from the fax service\'s report.')}>Try recording again</button>}
        {canRetry && canSettle && ' '}
        {canSettle && !settling && <button type="button" className="um-open" disabled={busy} onClick={() => setSettling(true)}>Settle this fax</button>}
      </p>
      {!canSettle && fax.resolver_is_sender && <p className="um-quiet">Someone other than the person who sent this fax must settle it.</p>}
      {settling && canSettle && (
        <div className="um-form" role="group" aria-label="Settle the earlier fax">
          <label><span>Reason (10 to 500 characters)</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Reason for settling" /></label>
          <label className="um-check"><input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} aria-label="I checked this myself" />
            <span>I checked this myself: it reached DCF, and it is settled without being recorded as the sending.</span></label>
          <div className="btns">
            <button type="button" className="um-open um-primary" disabled={busy || !attest || trimmed.length < 10 || trimmed.length > 500}
              onClick={() => run(() => umedApi.resolveFaxTransmission(caseId, fax.id, { resolution: 'ACKNOWLEDGED', reason: trimmed, attested: true }), 'Fax settled.')}>Settle</button>
            <button type="button" className="um-open" disabled={busy} onClick={() => setSettling(false)}>Cancel</button>
          </div>
        </div>
      )}
      {err && <p role="alert">{err}</p>}
    </div>
  );
}

export default function WaitingFaxes({ caseId, faxes, onDone, earlier = true }: { caseId: string; faxes?: Waiting[] | null; onDone: (msg: string) => void | Promise<void>; earlier?: boolean }) {
  if (!faxes || !faxes.length) return null;
  return <>{faxes.map((f) => <WaitingFax key={f.id} caseId={caseId} fax={f} onDone={onDone} earlier={earlier} />)}</>;
}

/** Delivered, not recorded as the sending, not settled: a fax that waits for a person. */
const waitsForPerson = (f: Waiting | null | undefined): f is Waiting => Boolean(f && f.status === 'DELIVERED' && f.sending === 'NEEDS_ATTENTION'
  && (!f.resolution || f.resolution.kind !== 'ACKNOWLEDGED'));

/**
 * PR #420 round 9: for a case with NO packet preparation (the panel above is not shown), the faxes of the case that
 * reached DCF and wait for a person -- read from the same status the panel reads (GET .../packet-preparation, which
 * answers with or without a preparation row), so the 409 FAX_AWAITING_RECORD always has something on the screen to
 * resolve. Every one of the case's waiting faxes, the current episode's own included (no panel shows it here).
 */
export function CaseWaitingFaxes({ caseId, onChanged }: { caseId: string; onChanged: (msg: string) => void | Promise<void> }) {
  const [faxes, setFaxes] = useState<Waiting[]>([]);
  const load = useCallback(async () => {
    const r = await umedApi.packetPreparation(caseId);
    if (!r.success || !r.data) { setFaxes([]); return; }
    const own = waitsForPerson(r.data.fax_transmission) ? [r.data.fax_transmission] : [];
    setFaxes([...own, ...(r.data.faxes_waiting || []).filter(waitsForPerson)]);
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!faxes.length) return null;
  return (
    <div className="um-section" aria-label="Faxes waiting for a person">
      <WaitingFaxes caseId={caseId} faxes={faxes} earlier={false} onDone={async (m) => { await load(); await onChanged(m); }} />
    </div>
  );
}
